import { toInputDate } from "@/utils/dateUtils";
import { api } from "@/integrations/clients/carAuction/carAuctionClient";
import {
  CreateTestRequest,
  CreateTestResponse,
  GetAllTestsResponse,
  UpdateTestRequest,
  UpdateTestResponse,
  UpdateTestStatusRequest,
  UpdateVehicleTestImagesRequest,
  VehicleTest,
} from "@/types/vehicleTest";
import { UserDetails } from "@/types/userDetails";

export class TestService {
  // Create a new test
  static async createTest(
    testData: CreateTestRequest,
  ): Promise<CreateTestResponse> {
    // INFO: testData includes bool "isDraft" for when user saves a draft. This should set status of created test to "ongoing"?
    try {
      // Backend returns { success: true, data: { id, vehicleName, ... } }
      const response = await api.post<{
        success: boolean;
        data: CreateTestResponse;
      }>("/tests", testData);

      // Backend response structure: { success: true, data: { id, ... } }
      if (response.success && response.data) {
        return response.data;
      }

      // Fallback if response structure is different
      throw new Error("Invalid response structure from backend");
    } catch (error) {
      throw error;
    }
  }

  // Get a specific test by ID
  static async getTestById(testId: string): Promise<VehicleTest | undefined> {
    const response = await api.get<{ success: boolean; data: VehicleTest }>(
      `/tests/${testId}`,
    );
    return response.data;
  }

  // Get vehicle tests with query support
  static async getVehicleTests(query?: {
    regNumber?: string;
    customerId?: string;
    page?: number;
    limit?: number;
  }): Promise<GetAllTestsResponse> {
    const queryParams = new URLSearchParams();
    if (query?.regNumber) queryParams.append("regNumber", query.regNumber);
    if (query?.customerId) queryParams.append("customerId", query.customerId);
    if (query?.page) queryParams.append("page", query.page.toString());
    if (query?.limit) queryParams.append("limit", query.limit.toString());

    const response = await api.get<{
      success: boolean;
      tests: VehicleTest[];
      total: number;
      page: number;
      limit: number;
    }>(`/tests?${queryParams.toString()}`);
    return {
      tests: response.tests,
      total: response.total,
      page: response.page,
      limit: response.limit,
    };
  }

  // Get all tests with pagination (Admin view)
  // status can be a single status string or comma-separated statuses (e.g., "pending-review,complement")
  static async getAllTestsForAdmin(
    page: number = 1,
    limit: number = 10,
    status?: string | string[],
    search?: string,
    includeArchived: boolean = false,
    sort?: string,
  ): Promise<GetAllTestsResponse> {
    let statusParam: string;
    if (!status) {
      statusParam = "ALL";
    } else if (Array.isArray(status)) {
      // Convert array to comma-separated string
      statusParam = status.join(",");
    } else {
      statusParam = status;
    }

    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      status: statusParam,
    });

    if (includeArchived) {
      params.append("includeArchived", "true");
    }

    if (search) {
      params.append("search", search);
    }

    if (sort) {
      params.append("sort", sort);
    }

    const response = await api.get<GetAllTestsResponse>(
      `/admin/tests?${params.toString()}`,
    );
    return response;
  }

  // Get user's own tests with pagination
  static async getAllUserTests(
    page: number = 1,
    limit: number = 10,
    includeArchived: boolean = false,
    sort?: string,
  ): Promise<GetAllTestsResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (includeArchived) {
      // Archive: show all archived tests regardless of status (own + colleagues for dealer/business)
      params.append("status", "ALL");
      params.append("includeArchived", "true");
    } else {
      params.append("status", "ALL");
    }
    if (sort) {
      params.append("sort", sort);
    }
    const response = await api.get<GetAllTestsResponse>(
      `/tests?${params.toString()}`,
    );

    return response;
  }

  // Update an existing test
  static async updateTestStatus(
    updateData: UpdateTestStatusRequest,
    id: string,
  ): Promise<void> {
    await api.put(`/admin/tests/${id}/status`, updateData);
  }

  /** Admin confirms that the requested change has been done; notifies user and sets status back to PENDING_REVIEW */
  static async confirmChangeRequest(testId: string): Promise<void> {
    await api.post(`/admin/tests/${testId}/confirm-change-request`);
  }

  // Update test
  // Update an existing test with full data
  static async updateTest(
    testId: string,
    updateData: UpdateTestRequest,
  ): Promise<UpdateTestResponse> {
    const response = await api.put<{
      success: boolean;
      data: UpdateTestResponse;
    }>(`/tests/${testId}/update`, updateData);
    if (!response.success || !response.data) {
      throw new Error("Invalid response structure from backend");
    }
    return response.data;
  }

  static async submitTest(testId: string): Promise<UpdateTestResponse> {
    const response = await api.put<{
      success: boolean;
      data: UpdateTestResponse;
    }>(`/tests/${testId}/submit`, {});
    if (!response.success || !response.data) {
      throw new Error("Failed to submit test: Server response missing data");
    }
    return response.data;
  }

  // Update vehicle test images separately
  static async updateVehicleTestImages(
    request: UpdateVehicleTestImagesRequest,
  ): Promise<{ status: string; message: string }> {
    const { CarTestService } =
      await import("@/services/carTest/carTestService");

    // IMPORTANT: Execute operations in correct order:
    // 1. First remove images and service details (to free up positions/resources)
    // 2. Then add new images and service details (can use freed positions)
    // 3. Then update positions and comments (can run in parallel)

    // Step 1: Remove images and service details first
    const removeImagePromises = request.removeImages.map((imageId) =>
      CarTestService.removeCarTestImage(request.testId, imageId),
    );

    // Remove service details
    const removeServiceDetailPromises = (
      request.removeServiceDetails || []
    ).map(async (serviceDetailId) => {
      await api.delete(
        `/tests/${request.testId}/service-details/${serviceDetailId}`,
      );
    });

    await Promise.all([...removeImagePromises, ...removeServiceDetailPromises]);

    // Step 2: Add new images and service details after removals are complete
    // Note: If any new image is set as thumbnail, backend will automatically unset all other thumbnails
    const uploadPromises = request.addImages.map(async (imageData) => {
      // Map section to category
      const categoryMap: Record<
        string,
        "exterior" | "interior" | "damage" | "service" | "tires"
      > = {
        exterior: "exterior",
        interior: "interior",
        damage: "damage",
        serviceImages: "service",
        tires: "tires",
      };
      const category = categoryMap[imageData.section] || "exterior";

      // Extract tireType for tires section
      const tireType =
        imageData.section === "tires"
          ? imageData.tireType || "primary"
          : undefined;

      return CarTestService.addCarTestImage(
        request.testId,
        category,
        imageData.file,
        imageData.isThumbnail,
        tireType as "primary" | "secondary" | undefined,
        imageData.section === "damage" ? imageData.comment || "" : undefined,
      );
    });

    // Add service details - format according to backend API
    const addServiceDetailPromises = (request.addServiceDetails || []).map(
      async (serviceDetail) => {
        await api.post(`/tests/${request.testId}/service-details`, {
          date:
            serviceDetail.date instanceof Date
              ? toInputDate(serviceDetail.date)
              : serviceDetail.date,
          mileage: serviceDetail.mileage,
          serviceType: serviceDetail.serviceType,
          notes: serviceDetail.notes || undefined,
        });
      },
    );

    await Promise.all([...uploadPromises, ...addServiceDetailPromises]);

    // Step 3: Update comments
    const commentPromises = (request.updateDamageComments || []).map((update) =>
      CarTestService.updateCarTestImageComment(
        request.testId,
        update.imageId,
        update.comment,
      ),
    );

    await Promise.all(commentPromises);

    return {
      status: "success",
      message: "Images updated successfully",
    };
  }

  // Get vehicle basic information by registration number (LPN)
  static async getVehicleBasicInformationByLpn(id: string) {
    const response = await api.get<{
      success: true;
      data: {
        car: {
          year: number;
          brand: string;
          car_model: string;
          hp?: number;
          body?: string;
          lpn: string;
          vin: string;
          fuel?: string;
          engineVolume?: string;
          color?: string;
          registrationDate?: string | null;
          lastInspectionDate?: string | null;
          annualVehicleTax?: number | null;
          seats?: number | null;
          owners?: number | null;
        };
        checkIfAllegedAttourneyInFact: boolean;
        doManualSearch: boolean;
      };
    }>(`/auto-uncle/${encodeURIComponent(id)}/basic-information-by-lpn`);
    return response;
  }

  // Get vehicle basic information by VIN/chassis number
  static async getVehicleBasicInformationByVin(id: string) {
    const response = await api.get<{
      success: true;
      data: {
        car: {
          year: number;
          brand: string;
          car_model: string;
          [key: string]: any;
        };
      };
    }>(`/auto-uncle/${encodeURIComponent(id)}/basic-information-by-vin`);

    return response;
  }

  // Request change for a test (approved / active-listing)
  static async requestTestChange(
    testId: string,
    message: string,
  ): Promise<{ status: string; message: string }> {
    const response = await api.post<{
      success: boolean;
      message?: string;
    }>(`/tests/${testId}/request-change`, { message });

    return {
      status: "success",
      message: response.message || "Ändringsbegäran skickad",
    };
  }

  /** Withdraw PENDING_REVIEW → ONGOING so the user can edit and resubmit */
  static async withdrawPendingReview(
    testId: string,
  ): Promise<{ status: string; message: string }> {
    const response = await api.post<{
      success: boolean;
      message?: string;
    }>(`/tests/${testId}/withdraw-review`, {});

    return {
      status: "success",
      message: response.message || "Testet är åter pågående",
    };
  }

  // Archive car tests
  static async archiveCarTests(
    ids: string[],
    isAdmin: boolean = false,
  ): Promise<{ success: boolean; message: string }> {
    const endpoint = isAdmin ? "/admin/tests/archive" : "/tests/archive";
    const response = await api.post<{
      success: boolean;
      message?: string;
    }>(endpoint, { ids });

    return {
      success: response.success,
      message: response.message || "Tester arkiverade",
    };
  }

  // Unarchive car tests
  static async unarchiveCarTests(
    ids: string[],
    isAdmin: boolean = false,
  ): Promise<{ success: boolean; message: string }> {
    const endpoint = isAdmin ? "/admin/tests/unarchive" : "/tests/unarchive";
    const response = await api.post<{
      success: boolean;
      message?: string;
    }>(endpoint, { ids });

    return {
      success: response.success,
      message: response.message || "Tester avarkiverade",
    };
  }

  // Get archived tests for admin - only show handled tests (approved, rejected, active-listing)
  static async getArchivedTestsForAdmin(
    page: number = 1,
    limit: number = 10,
    status?: string | string[],
    search?: string,
  ): Promise<GetAllTestsResponse> {
    // For archived tests, we only want handled statuses (approved, rejected, active-listing)
    const handledStatuses = ["approved", "rejected", "active-listing"];
    const statusParam = handledStatuses.join(",");

    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      status: statusParam,
      includeArchived: "true",
    });

    if (search) {
      params.append("search", search);
    }

    const response = await api.get<GetAllTestsResponse>(
      `/admin/tests?${params.toString()}`,
    );
    return response;
  }
}
